import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Button, Collapse, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { generatePath, Link as RouterLink } from 'react-router-dom';

import { BoardIssue } from '../../api/ach/board';
import { EDIT_AD_GROUP_URL, EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import useModal from '../../hooks/useModal';
import LineItemSpendModal from '../LineItems/LineItemSpendModal';
import ruleLabel from './ruleLabels';
import isIORule from './rules';
import { formatAge, formatAgo, SeverityIcon } from './severity';

const RESOLUTION_LABEL: Record<string, string> = {
  recovered: 'Recovered',
  no_longer_applicable: 'No longer applicable',
};

interface AlertRowProps {
  issue: BoardIssue;
  // The contract the issue belongs to — needed to build the ad-group edit link
  // (EDIT_AD_GROUP_URL is /admin/contracts/:id/adgroups/:adGroupId, :id = contract).
  contractId: number;
  // The insertion order this row belongs to (line-item page id). IO-grain rows link
  // here as their primary entity and open the daily-spend modal against it.
  insertionOrderId: number;
  // IO name for the primary link label on IO-grain rows; falls back to "Line item <id>".
  ioName?: string | null;
  onAck: (issueId: number) => void;
  acking?: boolean;
  // False when the contract group has a uniform trafficker shown once on the card
  // header (rollup) — the per-row trafficker is then omitted to reduce repetition.
  showTrafficker?: boolean;
}

// AlertRow renders one Sentinel issue: severity icon, title, an entity link, the
// trafficker (plain muted text; omitted under a card-level rollup), a demoted monospace
// rule slug, first_seen/last_seen ages, an Ack button (with ack attribution once
// acknowledged), and a collapsible evidence detail. IO-grain rows (io_* rules) treat the
// insertion order as the primary entity: the link points at the line-item page, the
// campaign is demoted to "via <campaign>" attribution text (a stable representative
// sibling, not the delivery anchor), and a "View daily spend" action opens the shared
// LineItemSpendModal for the IO. Campaign-grain rows link to the ad-group edit page.
// Resolved issues render read-only with their resolution reason and time instead of an
// Ack. Links that navigate away from the alerts page open in a new tab.
function AlertRow({
  issue,
  contractId,
  insertionOrderId,
  ioName,
  onAck,
  acking = false,
  showTrafficker = true,
}: AlertRowProps) {
  const isResolved = issue.state === 'resolved';
  const isAcknowledged = issue.state === 'acknowledged';
  const isIO = isIORule(issue.rule_id);
  const [showEvidence, setShowEvidence] = React.useState(false);
  const adGroupHref = generatePath(EDIT_AD_GROUP_URL, {
    id: String(contractId),
    adGroupId: String(issue.campaign_id),
  });
  const lineItemHref = generatePath(EDIT_LINE_ITEM_URL, {
    id: String(contractId),
    lineItemId: String(insertionOrderId),
  });

  const [showSpendModal, hideSpendModal] = useModal(
    () => (
      <LineItemSpendModal lineItemId={insertionOrderId} contractId={contractId} onClose={hideSpendModal} />
    ),
    { clickOutsideCloses: true },
    [insertionOrderId, contractId]
  );

  // Ack attribution — prefer the display name, fall back to the numeric id, and skip
  // the "by …" clause entirely when neither is present.
  const ackWho = issue.acknowledged_by_name ?? issue.acknowledged_by;
  const ackParts: string[] = [];
  if (ackWho) {
    ackParts.push(`by ${ackWho}`);
  }
  if (issue.acknowledged_at) {
    ackParts.push(new Date(issue.acknowledged_at).toLocaleDateString());
  }
  const ackAttribution = ackParts.join(' · ');
  const hasEvidence = issue.evidence.trim().length > 0;

  return (
    <Box data-testid={`alert-row-${issue.id}`} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" py={1} px={1} sx={{ flexWrap: 'wrap' }}>
        <SeverityIcon severity={issue.severity} />
        <Stack spacing={0.25} flexGrow={1} minWidth={200}>
          <Typography variant="body2" fontWeight={500}>
            {issue.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {isIO ? (
              <>
                <Link
                  component={RouterLink}
                  to={lineItemHref}
                  variant="caption"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`alert-io-link-${issue.id}`}
                >
                  {ioName || `Line item ${insertionOrderId}`}
                </Link>
                {(issue.campaign_name || issue.campaign_id) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={`alert-campaign-attr-${issue.id}`}
                  >
                    via {issue.campaign_name || `campaign ${issue.campaign_id}`}
                  </Typography>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={showSpendModal}
                  data-testid={`alert-spend-${issue.id}`}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400, lineHeight: 1.66 }}
                >
                  View daily spend
                </Button>
              </>
            ) : (
              <Link
                component={RouterLink}
                to={adGroupHref}
                variant="caption"
                target="_blank"
                rel="noopener noreferrer"
              >
                {issue.campaign_name || `Ad group ${issue.campaign_id}`}
              </Link>
            )}
            {showTrafficker && (
              <>
                <Typography variant="caption" color="text.secondary" aria-hidden>
                  ·
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.25}
                  alignItems="center"
                  data-testid={`alert-trafficker-${issue.id}`}
                >
                  <PersonOutlineIcon sx={{ fontSize: 14 }} color="disabled" />
                  <Typography variant="caption" color="text.secondary">
                    {issue.trafficker?.name ?? 'Unassigned'}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>

        <Stack spacing={0.25} alignItems="flex-end">
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            data-testid={`alert-type-${issue.id}`}
          >
            {ruleLabel(issue.rule_id)}
          </Typography>
          <Tooltip title={new Date(issue.first_seen).toLocaleString()}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatAge(issue.first_seen)}
            </Typography>
          </Tooltip>
          <Tooltip title={new Date(issue.last_seen).toLocaleString()}>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              data-testid={`alert-last-seen-${issue.id}`}
            >
              last seen {formatAgo(issue.last_seen)}
            </Typography>
          </Tooltip>
        </Stack>

        {isResolved ? (
          <Typography variant="caption" color="success.main" noWrap data-testid={`alert-resolution-${issue.id}`}>
            {RESOLUTION_LABEL[issue.resolution_reason ?? ''] ?? 'Resolved'}
            {issue.resolved_at ? ` · ${new Date(issue.resolved_at).toLocaleDateString()}` : ''}
          </Typography>
        ) : (
          <Stack spacing={0.25} alignItems="flex-end">
            <Button
              size="small"
              variant={isAcknowledged ? 'text' : 'outlined'}
              startIcon={isAcknowledged ? <CheckIcon /> : undefined}
              disabled={isAcknowledged || acking}
              onClick={() => onAck(issue.id)}
            >
              {isAcknowledged ? 'Acknowledged' : 'Ack'}
            </Button>
            {isAcknowledged && ackAttribution && (
              <Tooltip title={issue.acknowledged_at ? new Date(issue.acknowledged_at).toLocaleString() : ''}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  data-testid={`alert-ack-attr-${issue.id}`}
                >
                  {ackAttribution}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        )}

        {hasEvidence && (
          <IconButton
            size="small"
            onClick={() => setShowEvidence((v) => !v)}
            aria-label={showEvidence ? 'Hide evidence' : 'Show evidence'}
            aria-expanded={showEvidence}
            data-testid={`alert-evidence-toggle-${issue.id}`}
            sx={{ transform: showEvidence ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {hasEvidence && (
        <Collapse in={showEvidence} unmountOnExit>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ px: 1, pl: 5 }}>
            <Typography variant="caption" fontWeight={600} data-testid={`alert-evidence-heading-${issue.id}`}>
              {ruleLabel(issue.rule_id)}
            </Typography>
            <Tooltip title="Rule id (for debugging)">
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`alert-evidence-slug-${issue.id}`}
                sx={{ fontFamily: 'monospace' }}
              >
                {issue.rule_id}
              </Typography>
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            data-testid={`alert-evidence-${issue.id}`}
            sx={{ whiteSpace: 'pre-wrap', px: 1, pb: 1, pl: 5 }}
          >
            {issue.evidence}
          </Typography>
        </Collapse>
      )}
    </Box>
  );
}

export default AlertRow;
